(function(register){
    if (document.readyState !== 'loading') {
        register();
    } else {
        document.addEventListener('DOMContentLoaded', register, {
            once: true,
            passive: true,
        });
    }
})(function() {
    if (customElements.get('base-input-switch') === undefined) {
        const template = document.createElement('template');
        template.innerHTML = `
        <style>
        :host {
          --input-switch-inactive: #cccccc;
          --input-switch-active: black;
          --input-switch-fg: white;
          --input-switch-padding: .25em;
          --input-switch-focus-color: crimson;
          --input-switch-focus-outline: 4px solid var(--input-switch-focus-color);
          --input-switch-disabled-opacity: .25;
        
          display: inline-block;
          min-width: calc(3em + var(--input-switch-padding) * 2);
          vertical-align: middle;
        }
        :host([disabled]) {
            opacity: var(--input-switch-disabled-opacity);
        }
        :host([focus-visible]) [part="background"],
        :host(:focus-visible) [part="background"] {
          outline: var(--input-switch-focus-outline);
        }
        [part="background"] {
          background: var(--input-switch-inactive);
          color: var(--input-switch-fg);
          transition: background ease 200ms, color ease 200ms, outline ease 200ms;
          position: relative;
          z-index: 0;
          height: 1.5em;
          padding: var(--input-switch-padding);
          box-sizing: content-box;
          display: inline-flex;
          align-items: center;
          justify-content: space-between;
          width: calc(100% - var(--input-switch-padding) * 2);
        }
        [part="original"] {
          position: absolute;
          width: 1px;
          height: 1px;
          overflow: hidden;
          z-index: -1;
          opacity: .01;
        }
        [part="original"] ::slotted(input) {
          position: absolute;
          left: 1px;
          top: 1px;
        }
        :host([checked]) [part="background"] {
          background: var(--input-switch-active);
        }
        [part="yes"],
        [part="no"] {
          transition: opacity ease 100ms;
          position: relative;
          z-index: 1;
        }
        [part="yes"] {
          opacity: 0;
        }
        [part="yes"]:not(:empty) {
          margin-right: .5em;
        }
        [part="no"] {
          opacity: 1;
        }
        [part="no"]:not(:empty) {
          margin-left: .5em;
        }
        :host([checked]) [part="yes"] {
          opacity: 1;
        }
        :host([checked]) [part="no"] {
          opacity: 0;
        }
        [part="slider"] {
          position: absolute;
          top: var(--input-switch-padding);
          z-index: 2;
          width: 1.5em;
          height: 1.5em;
          left: var(--input-switch-padding);
          transition: left ease 200ms;
          background: var(--input-switch-fg);
        }
        :host([checked]) [part="slider"] {
          left: calc(100% - 1.5em - var(--input-switch-padding));
        }
        </style>
        <div part="background">
            <span part="yes">
                <slot name="yes-label"></slot>
            </span>
            <span part="no">
                <slot name="no-label"></slot>
            </span>
            <span part="slider">
                <slot name="slider-label"></slot>
            </span>
        </div>
        <div part="original">
            <slot></slot>
        </div>
        `;
        customElements.define('base-input-switch', class extends HTMLElement {
            constructor() {
                super();
                this.attachShadow({mode: "open"}).appendChild(template.content.cloneNode(true));
            }

            get defaultSlot() {
                return this.shadowRoot.querySelector('slot:not([name])');
            }

            get input() {
                const elements = this.defaultSlot.assignedElements();
                for (let element of elements) {
                    if (element instanceof HTMLInputElement && element.type === 'checkbox' || element.type === 'radio') {
                        return element;
                    }
                }
                return null;
            }

            set input(input) {
                Array.from(this.querySelectorAll(':scope > input:is([type="radio"],[type="checkbox"]):not([slot])')).forEach(child => {
                    this.removeChild(child);
                });
                this.insertAdjacentElement('beforeend', input);
            }

            makeSureInputExists() {
                if (!this.input) {
                    const input = document.createElement('input');
                    input.type = "checkbox";
                    if (this.getAttribute('checked') === 'checked') {
                        input.checked = true;
                    }
                    this.input = input;
                }
            }

            connectEvents(input) {
                if (input.checked) {
                    this.setAttribute('checked', 'checked');
                } else {
                    this.removeAttribute('checked');
                }
                input.addEventListener('change', () => {
                    if (input.checked) {
                        this.setAttribute('checked', 'checked');
                    } else {
                        this.removeAttribute('checked');
                    }
                });
                input.addEventListener('focus', () => {
                    if (this.input.matches(':focus-visible')) {
                        this.setAttribute('focus-visible', 'focus-visible');
                    }
                });
                input.addEventListener('blur', () => this.removeAttribute('focus-visible'));
                const setDisabled = () => {
                    if (input.disabled || input.hasAttribute('disabled')) {
                        this.setAttribute('disabled', 'disabled');
                    } else {
                        this.removeAttribute('disabled');
                    }
                };
                new MutationObserver(setDisabled).observe(input, {
                    attributes: true,
                    attributeFilter: ['disabled'],
                });
                setDisabled();
            }

            connectedCallback() {
                this.makeSureInputExists();
                this.addEventListener('click', (event) => {
                    const input = this.input;
                    if (input.disabled) {
                        return;
                    }
                    if (event.target === input) {
                        return;
                    }
                    if (event.target.closest('label') !== null) {
                        return;
                    }
                    input.checked = !input.checked;
                    input.dispatchEvent(new Event('change'));
                });
                let input = this.input;
                this.connectEvents(input);
                this.defaultSlot.addEventListener('slotchange', () => {
                    this.makeSureInputExists();
                    const newInput = this.input;
                    if (input !== newInput) {
                        this.connectEvents(newInput);
                        input = newInput;
                    }
                });
            }

            attributeChangedCallback(name, oldValue, newValue) {
                if (name === 'checked') {
                    this.input.checked = newValue === 'checked';
                    this.input.dispatchEvent(new Event('change'));
                } else if (name === 'disabled') {
                    if (newValue === 'disabled' && this.input.disabled === false) {
                        this.input.disabled = true;
                        this.input.setAttribute('disabled', 'disabled');
                    } else if (this.input.disabled === true) {
                        this.input.disabled = false;
                        this.input.removeAttribute('disabled');
                    }
                }
            }
        });
    }
});
